import camelCase from 'lodash/camelCase';

import processInclude from 'base/util';
import { applyForFinance } from './navigation/applyForFinance';
import { changeModel } from './navigation/changeModel';
import { uniqueBuildCode } from './navigation/uniqueBuildCode';
import { loadBuild } from './navigation/loadBuild';
import { shareBuild } from './navigation/shareBuild';
import { viewBrochure } from './navigation/viewBrochure';
import { showroomLocator } from './navigation/showroomLocator';
import { testDriveChooseModels } from './navigation/testDriveChooseModels';
import { testDriveHondaCentre } from './navigation/testDriveHondaCentre';
import { testDriveDetails } from './navigation/testDriveDetails';
import { testDriveConfirmation } from './navigation/testDriveConfirmation';
import { changeCentreCtas } from './changeCentreCtas';

/**
 * Wires the configurator navigation bar and its companion flyout modal.
 *
 * The flyout modal itself — re-parenting to `<body>`, the nested sliding sub-views, the back-history
 * stack and the `amb:modal-view-change(d)` events — is now owned by the reusable `amb-flyout`
 * directive (`directives/flyout.directive`), declared via `amb-flyout="configurator"` on the
 * `.configurator-modal` element in `flyoutViews.isml`. The directive exposes its `setView` /
 * `resetViews` API directly on the modal element, so this module drives it through the live DOM node
 * without importing the directive (which lives in a separate entry-point bundle).
 *
 * This module keeps only the navigation-specific concerns:
 *
 *   1. `[data-action]` CTAs — a convention-based custom handler (`handle{Name}Action`) on
 *      {@link navigation.helpers}, or a predefined CTA selector from {@link navigation.helpers.actionTriggerers}.
 *   2. The `amb:scroll-to-section` companion behaviour for the sticky nav.
 *   3. A thin facade ({@link navigation.helpers.setModalView} / {@link navigation.helpers.resetViews})
 *      that delegates to the flyout directive's controller, so existing callers (e.g. `configurator.js`)
 *      keep working unchanged.
 *
 * Loaded via SFRA's `processInclude` from `productDetail.js`, which iterates the top-level keys of this
 * object and invokes every value that is a function — in insertion order. The lifecycle methods MUST
 * stay in this exact order:
 *
 *   1. `selectElements()`                 — resolve nav / modal DOM refs needed by every later step
 *   2. `registerEventListeners()`         — bind delegated click & scroll listeners
 *   3. `initSubViews()`                   — bootstrap per-view sub-controllers (change-model, …)
 *   4. `applyChangeCentreDisabled()`       — disable Flow A Change Centre for footer hide/block reasons
 *
 * `helpers` is a plain object so `processInclude` skips it; it doubles as the state container and the
 * `this` context for all handlers.
 */
export const navigation = {
  /**
   * Resolve every selector in {@link navigation.helpers.elements} to its matching `HTMLElement` (or `null`), in place.
   */
  selectElements() {
    for (const [element, selector] of Object.entries(this.helpers.elements)) {
      this.helpers.elements[element] = document.querySelector(selector);
    }
  },

  /**
   * Bind delegated click / scroll listeners. Bound once, never removed.
   *
   * The action handler is bound to both the navigation and the modal so `[data-action]` CTAs work
   * from either location; `[data-view]` clicks are handled by the flyout directive, not here.
   */
  registerEventListeners() {
    const { modal, navigation } = this.helpers.elements;

    modal?.addEventListener('click', event => this.helpers.onNavigationClick(event));
    navigation?.addEventListener('click', event => this.helpers.onNavigationClick(event));
    navigation?.addEventListener('amb:scroll-to-section', event => this.helpers.onScrollToSection(event));
  },

  /**
   * Bootstrap every per-view sub-controller declared under `./navigation/`.
   *
   * Each sub-controller is a plain object with the same `processInclude`-friendly shape as this module: top-level functions are
   * invoked once at boot (in declaration order) and a `helpers` plain object is skipped. This is where view-specific logic lives so
   * the parent `navigation` module stays focused on routing.
   *
   * Sub-controllers subscribe to the flyout directive's `amb:modal-view-changed` events (dispatched on the modal, bubbling to
   * `document.body`) to lazy-wire themselves on first show of their view.
   *
   * Sub-controllers MUST be idempotent and tolerant of missing markup — `processInclude` runs them on every PDP regardless of which
   * views are actually rendered server-side for that flow.
   */
  initSubViews() {
    processInclude(changeModel);
    processInclude(loadBuild);
    processInclude(shareBuild);
    processInclude(uniqueBuildCode);
    processInclude(applyForFinance);
    processInclude(viewBrochure);
    processInclude(showroomLocator);
    processInclude(testDriveChooseModels);
    processInclude(testDriveHondaCentre);
    processInclude(testDriveDetails);
    processInclude(testDriveConfirmation);
  },

  /**
   * Disable Flow A Change Centre when any footer hide/block reason is active
   * (modify URL at boot; duplicate-tab / dealerCode / cross-border patch later).
   */
  applyChangeCentreDisabled() {
    changeCentreCtas.apply();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function);
   */
  helpers: {
    /**
     * DOM references. Declared as CSS selectors; replaced in place with `HTMLElement | null` by {@link navigation.selectElements} at boot.
     *
     * @type {Record<'modal' | 'navigation', HTMLElement | null>}
     */
    elements: {
      navigation: '.configurator-navigation',
      modal: '.configurator-modal'
    },

    /**
     * `data-action` → CSS selector of an existing CTA that already owns the real behaviour.
     * {@link navigation.helpers.onNavigationClick} forwards the click via `$.trigger('click')`.
     *
     * @type {Record<string, string>}
     */
    actionTriggerers: {
      'summary': '#configurator-footer-actions-summary',
      'next': '#configurator-footer-actions-summary',
      'update-order': '.modify-car-summary-update-order-cta',
      'save-to-cxp': '.configurator-send-offine-order-cta'
    },

    /**
     * Toggle `scroll-down` on the navigation root BEFORE the smooth scroll begins, so the matching `scroll-padding-top` is in place when the
     * browser settles on the target — otherwise a mid-scroll padding swap leaves the target parked at the wrong offset on mobile.
     *
     * @param {CustomEvent<{ target: Element }>} event
     */
    onScrollToSection(event) {
      const scrollPaddingTop = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const isScrollDown = event.detail.target.getBoundingClientRect().top > scrollPaddingTop;

      event.currentTarget.classList.toggle('scroll-down', isScrollDown);
    },

    /**
     * Build a convention-based handler method name on `this`.
     * Centralises the `handle-${name}-${suffix}` → camelCased convention.
     *
     * @param {string} prefix
     * @param {string} name
     * @param {string} suffix
     *
     * @returns {string} e.g. `'handleBackAction'`
     */
    resolveHandler(prefix, name, suffix) {
      return camelCase(`${prefix}-${name}-${suffix}`);
    },

    /**
     * Dispatch a click on the navigation / modal for `[data-action]` elements:
     *
     *   - `handle{Name}Action(event)` if defined, otherwise `actionTriggerers[name]`.
     *     Custom handler wins so an action declared in both isn't silently shadowed.
     *
     * `[data-view]` navigation is owned by the flyout directive; clicks outside an `<a>` / `<button>` or
     * without `data-action` are ignored here.
     *
     * @protected
     *
     * @param {MouseEvent} event
     */
    onNavigationClick(event) {
      const button = event.target.closest('a, button');

      if (!button || !button.dataset.action) {
        return;
      }

      if (button.classList.contains('duplicate-tab-blocker') || button.classList.contains('disabled')) {
        event.preventDefault();

        return;
      }

      const action = button.dataset.action;
      const customActionHandler = this.resolveHandler('handle', action, 'action');

      if (typeof this[customActionHandler] === 'function') {
        this[customActionHandler](event);
      } else if (this.actionTriggerers[action]) {
        $(this.actionTriggerers[action]).trigger('click');
      }
    },

    /**
     * Facade over the flyout directive: switch the configurator flyout to `view` (or go `back`).
     *
     * Delegates to the `setView` method the `amb-flyout` directive exposes on the modal element, so
     * existing callers such as `configurator.js` (`navigation.helpers.setModalView('showroom-locator')`)
     * keep working across entry-point bundles.
     *
     * @param {string | null} [view=null]
     */
    setModalView(view = null) {
      this.elements.modal?.setView?.(view);
    },

    /**
     * Facade over the flyout directive: reset the flyout to its initial view.
     */
    resetViews() {
      this.elements.modal?.resetViews?.();
    },

    /**
     * Custom handler for the "Back" action.
     * Strips `?showSummary` and navigates back to the configure step, or reloads in place.
     *
     * @protected
     *
     * @param {MouseEvent} event
     */
    handleBackAction(event) {
      const url = new URL(location.href);

      if (url.searchParams.has('showSummary')) {
        url.searchParams.delete('showSummary');

        location.href = url.toString();
      } else {
        location.reload();
      }
    }
  }
};
